import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { showChildMissingTermsPopup } from '~/actions';
import { WorkerTypePsaWeb } from '~/constants/ff.posthog';
import { needsPsa } from '~/domain/AcceptPSATerms';
import { useOfficeProfile } from '~/domain/OfficeMissingInfo';
import { useAppSelector } from '~/hooks/useAppSelector';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import { fireShiftRequestPsaPopupViewed } from '~/posthogs/events';
import { usePickedChildOffice } from '../hooks/usePickedChildOffice';

/**
 * Mounted once in the shift request layout (GOTU-7481). When a parent viewing as itself picks a child
 * in Shift Details' Office dropdown whose worker type is employee and whose Personnel Service Agreement
 * is not accepted, opens the PSA popup a refused post opens, so the parent agrees before it fills in the
 * shift rather than after it posts. The popup cannot be closed without agreeing. The selected office
 * itself is asked by the app-wide `AcceptTermsPopup`, and a refused post still catches a child whose
 * record could not be read. Returns whether another blocking office dialog must wait.
 */
export const usePsaGate = (): boolean => {
  const dispatch = useDispatch();
  const isEnabled = useFeatureFlag(WorkerTypePsaWeb);
  const { pickedChildId } = usePickedChildOffice();
  const isTermsPopupOpen = useAppSelector(
    (state) => state.multiday.isPSAMissing || state.multiday.isTCMissing,
  );
  const { data: childOffice } = useOfficeProfile({ officeId: pickedChildId, enabled: isEnabled });

  const childNeedingPsa =
    isEnabled && pickedChildId && childOffice && needsPsa(childOffice) ? pickedChildId : null;

  // The popup closes only once the PSA is accepted, so a child it opened for has agreed, even while
  // the re-read after the accept is still on its way. Also keeps StrictMode's second effect run from
  // opening it twice.
  const openedForRef = useRef(new Set<string>());

  const isOpeningPsa = !!childNeedingPsa && !openedForRef.current.has(childNeedingPsa);

  useEffect(() => {
    if (!childNeedingPsa || openedForRef.current.has(childNeedingPsa)) return;
    openedForRef.current.add(childNeedingPsa);
    dispatch(
      showChildMissingTermsPopup({
        isTCMissing: false,
        isPSAMissing: true,
        officeId: childNeedingPsa,
      }),
    );
    fireShiftRequestPsaPopupViewed();
  }, [childNeedingPsa, dispatch]);

  // Reserve the dialog before the opening effect dispatches, then keep it reserved until acceptance.
  // Do not wait for the profile refresh: it can fail after the agreement was successfully recorded.
  return isOpeningPsa || isTermsPopupOpen;
};
